import { useCaApiSdk } from '@/modules/CA/api-sdk/context/use-ca-api-sdk';
import { colors } from '@/styles/theme/foundations/colors';
import { WarningTwoIcon } from '@chakra-ui/icons';
import { Center, Spinner, Text, VStack, useColorMode, useToast } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { useAnalytics } from 'use-analytics';
import { CookieManager } from '../../../Auth/utils/cookie-manager';
import { useCaOrganization } from '../../organization/context/use-ca-organization';
import { useCaUser } from '../../user/context/use-ca-user';
import { CaAuthContext } from './ca-auth-context';

export const CaAuthProvider: React.FC<React.PropsWithChildren> = (props) => {
  const { reset } = useAnalytics();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const sdk = useCaApiSdk();
  const { clearUser } = useCaUser();
  const { setOrganizationId } = useCaOrganization();
  const toast = useToast();

  const { colorMode } = useColorMode();

  const signOut = useCallback(() => {
    CookieManager.removeCaCookies();
    clearUser();
    void reset();
  }, [clearUser, reset]);

  const { isLoading } = sdk.useVerifyJwt(
    'VerifyJwt',
    {},
    {
      onSuccess: (response) => {
        if (response?.verifyJwt) {
          setIsAuthenticated(true);

          if (response.verifyJwt.organizationId) {
            void setOrganizationId(response.verifyJwt.organizationId);
          } else {
            toast({
              title: 'Error',
              description: 'You need to select an organization. Redirecting to My Organizations...',
              status: 'error',
              duration: 2500,
              isClosable: true,
            });

            // redirect to CA my orgs in 2.5 seconds
            setTimeout(() => {
              window.location.replace(
                `${process.env.NEXT_PUBLIC_FRONTEND_DOMAIN_V1 as string}/my-organizations?redirect=${
                  window.location.href
                }`,
              );
            }, 2500);
          }
        }
      },
      onError: () => {
        // redirect to CA login page in 2.5 seconds
        setTimeout(() => {
          window.location.replace(
            `${process.env.NEXT_PUBLIC_FRONTEND_DOMAIN_V1 as string}/sign-in?redirect=${window.location.href}`,
          );
        }, 2500);
      },
    },
  );

  const memoizedValue = useMemo(() => {
    return { isAuthenticated, signOut };
  }, [isAuthenticated, signOut]);

  return (
    <CaAuthContext.Provider value={memoizedValue}>
      {isAuthenticated ? (
        props.children
      ) : (
        <Center minH="100vh" as={VStack} bg={colors.background.global[colorMode]}>
          {isLoading ? (
            // if not authenticated but loading, show loading spinner
            <>
              <Spinner />
              <Text>Verifying credentials...</Text>
            </>
          ) : (
            // if not authenticated and not loading, we will need to redirect to CA login page
            <>
              <WarningTwoIcon />
              <Text>Unable to verify credentials. Redirecting...</Text>
            </>
          )}
        </Center>
      )}
    </CaAuthContext.Provider>
  );
};
